import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI, getErrorMessage } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, CheckCircle, XCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setTokenError('Token non fornito');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await authAPI.verifyResetToken(token);
      setTokenValid(true);
    } catch (error) {
      setTokenError(getErrorMessage(error, 'Token non valido o scaduto'));
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setLoading(true);

    try {
      await authAPI.resetPassword({ token, new_password: password });
      setSuccess(true);
      toast.success('Password reimpostata con successo!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel reset della password'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
        <div className="text-[#ccff00] text-xl font-bold">Verifica token...</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-black text-[#ccff00] tracking-tight mb-2">
              Trainers
            </h1>
          </div>

          <div className="glass-card p-8 rounded-sm text-center">
            <CheckCircle className="w-16 h-16 text-[#ccff00] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Password Reimpostata!</h2>
            <p className="text-zinc-400 mb-6">
              La tua password è stata cambiata con successo. Ora puoi accedere con la nuova password.
            </p>
            <Link to="/login">
              <Button className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12">
                Vai al Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-4xl font-black text-[#ccff00] tracking-tight mb-2">
              Trainers
            </h1>
          </div>

          <div className="glass-card p-8 rounded-sm text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Link Non Valido</h2>
            <p className="text-zinc-400 mb-6">
              {tokenError || 'Il link per reimpostare la password non è valido o è scaduto.'}
            </p>
            <Link to="/forgot-password">
              <Button className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12">
                Richiedi Nuovo Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#ccff00] tracking-tight mb-2">
            Trainers
          </h1>
          <p className="text-zinc-400">Imposta la nuova password</p>
        </div>

        <div className="glass-card p-8 rounded-sm space-y-6">
          <div className="text-center">
            <Lock className="w-12 h-12 text-[#ccff00] mx-auto mb-4" />
            <p className="text-zinc-400 text-sm">
              Inserisci la tua nuova password. Assicurati che sia sicura e facile da ricordare.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Nuova Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
                required
                minLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-zinc-300">Conferma Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-sm h-12 text-lg"
                required
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-sm h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)] hover:scale-[1.02] transition-all"
            >
              {loading ? 'Salvataggio...' : 'Salva Nuova Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
